import { useCreateEmbedAttachment } from "@/api/attachment";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import StyledDrawer from "@/components/StyledDrawer.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch.tsx";
import { toast } from "@/hooks/use-toast";
import { optionsPosition, TypeOptionPosition } from "@/lib/image";
import { parseEmbedUrl } from "@/lib/media/embed";
import { mediaLibraryTranslationKey } from "@/lib/media/media-library-labels";
import { Youtube } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface AddEmbedFormProps {
  docId: string;
  type: TypeOptionPosition;
  instanceId: string;
  onSuccess?: () => void;
  disabled?: boolean;
  /** When true, the drawer is controlled externally (no trigger button). */
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  embeddedInDrawer?: boolean;
}

/** Quick client-side check for a YouTube/Vimeo URL (server re-validates). */
function isLikelyEmbedUrl(url: string): boolean {
  return /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(url.trim());
}

const AddEmbedForm = ({
  docId,
  type,
  instanceId,
  onSuccess,
  disabled,
  hideTrigger = false,
  open: controlledOpen,
  onOpenChange,
  embeddedInDrawer = false,
}: AddEmbedFormProps) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [url, setUrl] = useState("");
  const [position, setPosition] = useState("");
  const [videoLoop, setVideoLoop] = useState(false);
  const { mutateAsync: createEmbed, isPending } = useCreateEmbedAttachment();

  const reset = () => {
    setUrl("");
    setPosition("");
    setVideoLoop(false);
  };

  const handleSubmit = async () => {
    if (!isLikelyEmbedUrl(url)) {
      toast({
        variant: "error",
        description: t("mediaLibrary.embed.invalidUrl"),
        duration: 2500,
      });
      return;
    }
    if (!position) {
      toast({
        variant: "error",
        description: t("attachment.form.position.required"),
        duration: 2500,
      });
      return;
    }
    try {
      await createEmbed({
        url: url.trim(),
        docId,
        position,
        instanceId,
        videoLoop,
      });
      toast({
        variant: "success",
        description: t(mediaLibraryTranslationKey("uploadSuccess", "embed")),
      });
      reset();
      setOpen(false);
      onSuccess?.();
    } catch {
      toast({
        variant: "error",
        description: t("mediaLibrary.embed.invalidUrl"),
        duration: 2500,
      });
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
    }
    setOpen(nextOpen);
  };

  return (
    <StyledDrawer
      className="min-w-[500px]"
      maxWidth={embeddedInDrawer ? "sm:max-w-2xl" : undefined}
      title={t("mediaLibrary.embed.title")}
      sheetProps={{ open, onOpenChange: handleOpenChange }}
      trigger={
        hideTrigger ? undefined : (
          <Button
            variant="outline"
            className="font-semibold flex gap-2"
            disabled={disabled}
          >
            <Youtube className="h-4 w-4" />
            {t("mediaLibrary.embed.add")}
          </Button>
        )
      }
    >
      <div className="w-full flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          {t("mediaLibrary.embed.help")}
        </p>
        <Input
          label={t("mediaLibrary.embed.urlLabel")}
          placeholder="https://www.youtube.com/watch?v=..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        {parseEmbedUrl(url) && (
          <EmbedPlayer
            embedUrl={url}
            videoLoop={videoLoop}
            className="w-full"
          />
        )}
        <div className="flex items-center justify-between gap-3 rounded-md border p-3">
          <Label htmlFor="embed-video-loop" className="text-sm font-medium">
            {t("cms.generalSettings.player.loop")}
          </Label>
          <Switch
            id="embed-video-loop"
            checked={videoLoop}
            onCheckedChange={setVideoLoop}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">
            {t("attachment.form.position")}
          </label>
          <Select value={position} onValueChange={setPosition}>
            <SelectTrigger>
              <SelectValue
                placeholder={t("attachment.form.position.placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {optionsPosition[type].map((p: string) => (
                <SelectItem key={p} value={p}>
                  {t(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" onClick={handleSubmit} disabled={isPending}>
          {t(mediaLibraryTranslationKey("submit", "embed"))}
        </Button>
      </div>
    </StyledDrawer>
  );
};

export default AddEmbedForm;
